import { CreateControllerFn } from '@wix/yoshi-flow-editor';
import { FormApi } from '../../api/FormApi';
import { WaiverFormStatus } from '../../types/form-types';
import { type FormSubmission } from '@wix/ambassador-forms-v4-submission/types';

export enum ViewMode {
  OVERLAY = 'overlay',
  PAGE = 'page',
}

interface WaiverFormState {
  status: WaiverFormStatus;
  formId?: string;
  waiverForms?: {
    initialValues: Record<string, any>;
    overrides: Record<string, any>;
    fieldProperties: Record<string, any>;
  };
  formErrors?: { messageKey?: string; message?: string; field: string }[];
  onSubmitSuccess?: (submission: FormSubmission) => void;
  onLightboxClose?: () => void;
  viewMode?: ViewMode;
}

const FormPredifiendFieldNames = {
  firstName: 'first_name',
  lastName: 'last_name',
  email: 'email',
};

const createController: CreateControllerFn = async ({ flowAPI, appData }) => {
  // For more info about controller structure,
  // check the docs: https://bo.wix.com/pages/yoshi/docs/editor-flow/structure-api/component/ooi-component#controller

  const pageReady = async () => {
    const { controllerConfig } = flowAPI;
    const { setProps } = controllerConfig;
    const url = new URL(controllerConfig.wixCodeApi.location.url);
    let userEmail = null;
    try {
      const user = flowAPI?.controllerConfig.wixCodeApi.user.currentUser;
      userEmail = await user.getEmail();
    } catch (error) {}

    // Extract parameters from URL or appData
    const formId = appData?.formId || url.searchParams.get('formId');
    const onSubmitSuccess = appData?.onSubmitSuccess;
    const onLightboxClose = appData?.onLightboxClose;
    const viewMode =
      appData?.viewMode || (onLightboxClose ? 'overlay' : 'page');
    const token = url.searchParams.get('token');

    if (formId) {
      try {
        controllerConfig.wixCodeApi.user.onLogin(() => {
          controllerConfig.wixCodeApi.location.to?.(
            controllerConfig.wixCodeApi.location?.url!,
          );
        });

        // Initialize FormApi with REAL flowAPI
        const formApi = new FormApi({
          flowApi: flowAPI, // Use the REAL flowAPI, not mocks!
          httpClient: flowAPI.httpClient,
          experiments: flowAPI.experiments,
          reportError: (error: string | Error) => {
            return undefined;
          },
        });

        // Initialize form with real API call
        const [fieldProperties, userDetails] = await Promise.all([
          formApi.initWaiverFormController({
            formId,
          }),
          formApi.decryptUserDetails({
            token,
          }),
        ]);

        // Calculate initial values (placeholder for now)
        const initialValues = appData?.initialValues || {
          ...(userEmail ? { [FormPredifiendFieldNames.email]: userEmail } : {}),
          ...(userDetails?.email
            ? { [FormPredifiendFieldNames.email]: userDetails.email }
            : {}),
          ...(userDetails?.firstName
            ? { [FormPredifiendFieldNames.firstName]: userDetails.firstName }
            : {}),
          ...(userDetails?.lastName
            ? { [FormPredifiendFieldNames.lastName]: userDetails.lastName }
            : {}),
        };
        const overrides = {
          ...(userEmail || userDetails?.email
            ? { [FormPredifiendFieldNames.email]: { readOnly: true } }
            : {}),
        };

        const initialState: WaiverFormState = {
          status: WaiverFormStatus.IDLE,
          formId,
          waiverForms: {
            initialValues,
            overrides,
            fieldProperties,
          },
          formErrors: [],
          onSubmitSuccess,
          onLightboxClose,
          viewMode: viewMode || ViewMode.PAGE,
        };

        setProps({
          ...initialState,
          fitToContentHeight: true,
        });
      } catch (error) {
        setProps({
          status: WaiverFormStatus.ERROR,
          formId,
          formErrors: [
            {
              messageKey: 'waivers-sign-form.load-error',
              field: 'general',
            },
          ],
          fitToContentHeight: true,
        });
      }
    } else {
      setProps({
        status: WaiverFormStatus.ERROR,
        formErrors: [
          {
            messageKey: flowAPI.environment.isEditor
              ? 'waivers-sign-form.editor-no-form-id-error'
              : 'waivers-sign-form.no-form-id-error',
            field: 'general',
          },
        ],
        fitToContentHeight: true,
      });
    }
  };

  return {
    pageReady,
  };
};

export default createController;
